<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>Upcoming Invoices</page-header>

        <div class="text-sm text-gray-600 bg-blue-50 p-4 rounded-lg border border-blue-100">
            Final invoices are calculated shortly after the end of each billing period and may differ from the estimates
            displayed here. These estimates exclude any applicable sales tax.
        </div>

        <div v-if="loadingProforma" class="py-3 text-center">
            <vx-icon-loading class="w-9 h-9" spin></vx-icon-loading>
        </div>

        <template v-else>
            <proforma-invoice-card
                v-if="previousMonthInvoice"
                :invoice="previousMonthInvoice"
                :invoice-date="previousMonthDate"
                type="previous"
                :payment-terms-text="paymentTermsText"
            />

            <proforma-invoice-card
                v-if="currentMonthInvoice"
                :invoice="currentMonthInvoice"
                :invoice-date="currentMonthDate"
                type="current"
                :payment-terms-text="paymentTermsText"
            />

            <vx-alert v-if="!previousMonthInvoice && !currentMonthInvoice" color="info" :closeable="false">
                No upcoming invoices available at this time.
            </vx-alert>
        </template>
    </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { VxAlert, VxIconLoading } from '@voxie/frontend-components';
import ProformaInvoiceCard from './ProformaInvoiceCard.vue';
import { teamBillingClient } from '~/services';
import { useBillingDates } from '~/composables/useBillingDates';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import PageHeader from '~/components/general/PageHeader.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    paymentTermsDays: {
        type: Number,
        required: true,
    },
});

const toasted = useToasted();

const loadingProforma = ref(false);
const previousMonthInvoice = ref(null);
const currentMonthInvoice = ref(null);
const previousMonthDate = ref(null);
const currentMonthDate = ref(null);

const { isWithinEarlyMonth, getCurrentBillingMonth, getPreviousBillingMonth } = useBillingDates();

const paymentTermsText = computed(() => {
    if (props.paymentTermsDays === 0) {
        return 'Auto-pay on card';
    }

    return `Net ${props.paymentTermsDays} terms`;
});

const fetchProformaInvoices = async () => {
    loadingProforma.value = true;
    try {
        const shouldFetchPrevious = isWithinEarlyMonth();

        const currDate = getCurrentBillingMonth();

        const requests = [teamBillingClient.getProformaInvoice(props.teamId, currDate)];

        if (shouldFetchPrevious) {
            const prevDate = getPreviousBillingMonth();
            requests.push(teamBillingClient.getProformaInvoice(props.teamId, prevDate));
            previousMonthDate.value = prevDate;
        }

        const responses = await Promise.all(requests);

        currentMonthDate.value = currDate;
        currentMonthInvoice.value = responses[0]?.data;

        if (shouldFetchPrevious && responses[1]) {
            previousMonthInvoice.value = responses[1]?.data;
        } else {
            previousMonthInvoice.value = null;
            previousMonthDate.value = null;
        }
    } catch (e) {
        console.error('Failed to fetch proforma invoices:', e);
        toasted.error(catchRequestError(e));
        previousMonthInvoice.value = null;
        currentMonthInvoice.value = null;
        previousMonthDate.value = null;
        currentMonthDate.value = null;
    } finally {
        loadingProforma.value = false;
    }
};

onMounted(() => {
    fetchProformaInvoices();
});
</script>
